import { get } from 'lodash';
import createCachedSelector from 're-reselect';

import { getInternalBannerFieldsData } from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import { mapInternalBannerErrors } from '../../service/InternalBannerHelpers/InternalBannerHelpers';
import { BannerId } from '../../typings/banners';
import { CampaignId } from '../../typings/domain';
import {
    InternalBannerBaseInfo,
    InternalPlaceTemplates,
    InternalTemplateId,
    InternalTemplateResource,
    InternalTemplateResourceRestrictions
} from '../../typings/internalBanners';
import { EditedInternalBanner, EditInternalBannersState } from '../../typings/internalBannersEditState';

const getEditedBannerCacheKey = (state: EditInternalBannersState, bannerId: BannerId): BannerId => bannerId;

export const selectInternalBannerResources = (
    state: EditInternalBannersState,
    campaignId: CampaignId,
    templateId: InternalTemplateId
): InternalTemplateResource[] | undefined => {
    const resources = state.templateResources[campaignId];
    const record = resources && resources[templateId];

    return record && record.resources;
};

export const selectInternalBannerResourceRestrictions = (
    state: EditInternalBannersState,
    campaignId: CampaignId,
    templateId: InternalTemplateId
): InternalTemplateResourceRestrictions[] | undefined => {
    const resources = state.templateResources[campaignId];
    const record = resources && resources[templateId];

    return record && record.resourceRestrictions;
};

export const selectEditedInternalBanner = (state: EditInternalBannersState, bannerId: BannerId): EditedInternalBanner =>
    state.banners[bannerId];

export const selectEditedInternalBannerPlaceTemplates = (state: EditInternalBannersState): InternalPlaceTemplates =>
    state.placeTemplates;

export const selectEditedInternalBannerTemplateResources = (
    state: EditInternalBannersState,
    bannerId: BannerId
): InternalTemplateResource[] | undefined => {
    const banner = state.banners[bannerId];

    return selectInternalBannerResources(state, banner.campaign.id, banner.templateId);
};

export const selectInternalBannerBaseInfo = createCachedSelector(
    selectEditedInternalBanner,
    selectEditedInternalBannerPlaceTemplates,
    selectEditedInternalBannerTemplateResources,
    (banner, placeTemplates, templateResources): InternalBannerBaseInfo => {
        const { templateId, templateVars } = banner;
        const templateVarsErrors = get(banner, 'errors.templateVars', {});
        const placeId = banner.campaign.placeId;
        const availableTemplateIds = placeTemplates[placeId] || [];

        return {
            templateId,
            availableTemplateIds,
            fieldsData: getInternalBannerFieldsData(templateId, templateResources, templateVars),
            errors: {
                fieldsData: Object.keys(templateVarsErrors).map(key => templateVarsErrors[key].errors[0]),
                templateId:
                    availableTemplateIds.length === 0
                        ? [
                              {
                                  text: mapInternalBannerErrors('wrong-place-id')
                              }
                          ]
                        : get(banner, 'errors.templateId.errors', [])
            },
            isNew: banner.isNew,
            isDuplicateTemplate: banner.isDuplicateTemplate
        };
    }
)(getEditedBannerCacheKey);
